import React from 'react';
/**
 * Provides keyboard reordering logic for tabs in a tab bar using "[" and "]" keys.
 * Allows reordering the currently selected tab and returning focus to it after the reorder.
 *
 * @param {object} props - The props object.
 * @param {GlobalTab[]} props.tabs - List of all current tabs.
 * @param {string | undefined} props.selectedTabPath - The path of the currently selected tab.
 * @param {(tabs: GlobalTab[]) => void} [props.onChangeTabs] - Callback to update the tab order.
 * @param {React.RefObject<HTMLDivElement | null>} rootRef - Reference to the DOM element containing the tab buttons.
 *
 * @returns {{
 *   showKeyboardTooltip: boolean;
 *   setShowKeyboardTooltip: React.Dispatch<React.SetStateAction<boolean>>;
 *   handleKeyDown: (event: React.KeyboardEvent) => void;
 * }} Object containing tooltip visibility state, its setter, and keydown handler for tab reordering.
 */
export function useKeyboardReorder({ onChangeTabs, tabs, selectedTabPath }, rootRef) {
    const [showKeyboardTooltip, setShowKeyboardTooltip] = React.useState(false);
    const handleKeyDown = React.useCallback((event) => {
        const index = tabs.findIndex((tab) => tab.path === selectedTabPath);
        if (index !== -1 && onChangeTabs) {
            const focusActiveTab = () => {
                window.setTimeout(() => {
                    var _a, _b;
                    if (selectedTabPath) {
                        (_b = (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`button[data-tab-path="${selectedTabPath}"]`)) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                }, 0);
            };
            switch (event.key) {
                case '[':
                    if (index > 0) {
                        onChangeTabs([...tabs.slice(0, index - 1), tabs[index], tabs[index - 1], ...tabs.slice(index + 1)]);
                        focusActiveTab();
                    }
                    break;
                case ']':
                    if (index < tabs.length - 1) {
                        onChangeTabs([...tabs.slice(0, index), tabs[index + 1], tabs[index], ...tabs.slice(index + 2)]);
                        focusActiveTab();
                    }
                    break;
            }
        }
    }, [onChangeTabs, tabs, selectedTabPath, rootRef]);
    return { showKeyboardTooltip, setShowKeyboardTooltip, handleKeyDown };
}
